/**
 * Ajustes — DESKTOP: dos columnas, todo visible sin scroll en una pantalla normal.
 *
 * Dos cosas que acá se hacen y en el celular no tendría sentido hacer:
 * - El **mapa de máquinas del gimnasio**, que es un trabajo de teclado —etiquetas de ubicación,
 *   incrementos de placa, rangos— y no algo que nadie vaya a hacer parado entre series. §2 lo
 *   define como activo compartido, así que aportarlo desde el PC beneficia a todos.
 * - El **editor de rutina abierto**, sin plegar: hay ancho para ver los días uno al lado del otro
 *   y cambiarlos con el teclado, que es exactamente lo que no se puede hacer en 390 px.
 */

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { fechaLarga, formatearNumero, type Sexo, type Unidad } from '@mg/core';

import { aportarMaquina } from '../features/acciones.ts';
import { objetivoOpcional, useAjustes } from '../features/useAjustes.ts';
import { useRutina, type DiaDeRutina } from '../features/useRutina.ts';
import { useEstadoSync } from '../ui/IndicadorSync.tsx';
import { useVariante } from '../ui/layout-variant.tsx';
import { Aviso, Campo, Icono, Insignia, Selector, Vacio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';
import {
  ExportarCSV,
  FormularioClave,
  MarcaGuardado,
  SalirDeLaCuenta,
  SeccionAyuda,
} from './Ajustes.mobile.tsx';

export function AjustesDesktop({ onSalir }: { onSalir: () => Promise<void> }): ReactNode {
  const a = useAjustes();
  const { detalle } = useRuta();
  const { variante, preferencia, fijar, detectada } = useVariante();
  const sync = useEstadoSync();

  const refRutina = useRef<HTMLElement | null>(null);
  const refObjetivos = useRef<HTMLElement | null>(null);
  const listo = !a.datos.cargando && a.datos.perfil !== null;

  // El ancla de navegación: "se edita en Ajustes" es un botón que llega hasta acá, y tiene que
  // dejar la sección a la vista aunque esté a media pantalla de scroll.
  useEffect(() => {
    if (!listo) return;
    const destino =
      detalle === 'rutina' ? refRutina.current : detalle === 'objetivos' ? refObjetivos.current : null;
    destino?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [detalle, listo]);

  if (a.datos.cargando || !a.datos.perfil) return <Vacio>Cargando…</Vacio>;
  const p = a.datos.perfil;

  return (
    <div className="d-rejilla d-rejilla--2" style={{ alignItems: 'start' }}>
      <div className="columna" style={{ gap: 'var(--e4)' }}>
        <section className="tarjeta">
          <span className="eyebrow">Perfil</span>
          <div className="d-rejilla d-rejilla--2" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
            <div>
              <Campo
                etiqueta="Nombre"
                texto
                defaultValue={p.nombre}
                onBlur={(e) => void a.guardar({ nombre: e.target.value }, 'nombre')}
              />
              <MarcaGuardado visible={a.guardado === 'nombre'} />
            </div>
            <div>
              <Campo
                etiqueta="Altura (cm)"
                inputMode="numeric"
                defaultValue={p.altura_cm ?? ''}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  void a.guardar({ altura_cm: Number.isFinite(n) && n > 0 ? n : null }, 'altura');
                }}
              />
              <MarcaGuardado visible={a.guardado === 'altura'} />
            </div>
            <div>
              <Selector
                etiqueta="Sexo"
                value={p.sexo}
                onChange={(e) => void a.guardar({ sexo: e.target.value as Sexo }, 'sexo')}
              >
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
                <option value="otro">Prefiero no decirlo</option>
              </Selector>
              <MarcaGuardado visible={a.guardado === 'sexo'} />
            </div>
            <div>
              <Selector
                etiqueta="Unidad preferida"
                value={p.unidad_preferida}
                onChange={(e) => void a.guardar({ unidad_preferida: e.target.value as Unidad }, 'unidad')}
              >
                <option value="kg">Kilos</option>
                <option value="lb">Libras</option>
              </Selector>
              <p className="nota--fina" style={{ marginTop: 4 }}>
                Las series se muestran como las leíste en la máquina (esa unidad no se convierte).
                Esto cambia el peso corporal y los totales.
              </p>
              <MarcaGuardado visible={a.guardado === 'unidad'} />
            </div>
            <div>
              <Selector
                etiqueta="Fórmula de e1RM"
                value={p.formula_e1rm}
                onChange={(e) => void a.guardar({ formula_e1rm: e.target.value as 'epley' | 'brzycki' }, 'formula')}
              >
                <option value="epley">Epley</option>
                <option value="brzycki">Brzycki</option>
              </Selector>
              <MarcaGuardado visible={a.guardado === 'formula'} />
            </div>
            <div>
              <Campo
                etiqueta="Zona horaria"
                texto
                defaultValue={p.zona_horaria}
                onBlur={(e) => void a.guardar({ zona_horaria: e.target.value }, 'zona')}
                ayuda="Define cuándo cambia el día para el calendario."
              />
              <MarcaGuardado visible={a.guardado === 'zona'} />
            </div>
          </div>

          {a.alturaSugerida && (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Aviso tono="info">
                Tu balanza reporta IMC, así que la altura se puede despejar: {a.alturaSugerida} cm.
                <button
                  type="button"
                  className="boton boton--chico"
                  style={{ marginTop: 8 }}
                  onClick={() => void a.guardar({ altura_cm: a.alturaSugerida }, 'altura')}
                >
                  Usar {a.alturaSugerida} cm
                </button>
              </Aviso>
            </div>
          )}

          {a.error && (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Aviso tono="importante">{a.error}</Aviso>
            </div>
          )}
        </section>

        <SeccionRutina refSeccion={refRutina} />

        <SeccionObjetivos a={a} refSeccion={refObjetivos} />

        <MapaDeMaquinas a={a} />

        <SeccionAyuda />
      </div>

      <div className="columna" style={{ gap: 'var(--e4)' }}>
        <section className="tarjeta">
          <span className="eyebrow">Interfaz</span>
          <p className="nota" style={{ marginTop: 4 }}>
            Estás en la versión <strong>{variante === 'mobile' ? 'de celular' : 'de escritorio'}</strong>. En
            automático se decide por ancho de pantalla y tipo de puntero; ahora detecta{' '}
            {detectada === 'mobile' ? 'celular' : 'escritorio'}.
          </p>
          <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
            {(['auto', 'mobile', 'desktop'] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={preferencia === v ? 'boton boton--chico boton--principal crece' : 'boton boton--chico crece'}
                onClick={() => fijar(v)}
              >
                {v === 'auto' ? 'Automático' : v === 'mobile' ? 'Celular' : 'Escritorio'}
              </button>
            ))}
          </div>
          <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
            Forzar "Celular" acá sirve para revisar la interfaz de gimnasio sin sacar el teléfono.
          </p>
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Tema</span>
          <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
            <button
              type="button"
              className={a.tema === 'oscuro' ? 'boton boton--chico boton--principal crece' : 'boton boton--chico crece'}
              onClick={() => a.setTema('oscuro')}
            >
              Oscuro
            </button>
            <button
              type="button"
              className={a.tema === 'claro' ? 'boton boton--chico boton--principal crece' : 'boton boton--chico crece'}
              onClick={() => a.setTema('claro')}
            >
              Claro
            </button>
          </div>
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Sincronización</span>
          <p className="nota" style={{ marginTop: 4 }}>
            {sync.enLinea ? 'Con conexión.' : 'Sin conexión.'}{' '}
            {sync.pendientes > 0
              ? `${sync.pendientes} ${sync.pendientes === 1 ? 'cambio' : 'cambios'} por subir.`
              : 'Todo subido.'}
          </p>

          {sync.conError.length > 0 && (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Aviso tono="importante" titulo="El servidor rechazó algunos datos">
                <table className="tabla" style={{ marginTop: 4 }}>
                  <tbody>
                    {sync.conError.map((s) => (
                      <tr key={s.id}>
                        <td style={{ fontSize: 12 }}>{s.tabla}</td>
                        <td className="nota--fina">{s.ultimoError}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                Quedan guardados en local para que los corrijas; no se perdieron.
              </Aviso>
            </div>
          )}

          <button
            type="button"
            className="boton"
            style={{ marginTop: 'var(--e3)' }}
            onClick={() => void sync.sincronizarAhora()}
            disabled={sync.sincronizando}
          >
            <Icono nombre="nube" tamano={17} />
            {sync.sincronizando ? 'Sincronizando…' : 'Sincronizar ahora'}
          </button>
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Tus datos</span>
          <p className="nota" style={{ marginTop: 4 }}>
            Exportación completa, sin conversiones ni redondeos: los pesos salen tal como los
            registraste, con su unidad.
          </p>
          <button type="button" className="boton" style={{ marginTop: 'var(--e3)' }} onClick={a.exportarTodoJSON}>
            Exportar todo (JSON)
          </button>
          <ExportarCSV a={a} />
        </section>

        <FormularioClave />

        <section className="tarjeta">
          <span className="eyebrow">Cuenta</span>
          <div style={{ marginTop: 'var(--e3)' }}>
            <SalirDeLaCuenta a={a} onSalir={onSalir} />
          </div>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rutina y plan — DESKTOP
// ---------------------------------------------------------------------------

/**
 * La sección que cinco textos de la app nombraban sin que existiera. Acá va abierta: en el PC hay
 * ancho para ver los días en paralelo, y editar una rutina es trabajo de teclado.
 */
function SeccionRutina({ refSeccion }: { refSeccion: RefObject<HTMLElement | null> }): ReactNode {
  const r = useRutina();

  return (
    <section className="tarjeta" ref={refSeccion}>
      <div className="fila fila--entre">
        <span className="eyebrow">Rutina y plan</span>
        <Insignia>
          {r.dias.length} {r.dias.length === 1 ? 'día' : 'días'} · {r.totalEjercicios}{' '}
          {r.totalEjercicios === 1 ? 'ejercicio' : 'ejercicios'}
        </Insignia>
      </div>
      <p className="nota" style={{ marginTop: 4 }}>
        {r.rutina?.nombre ?? 'Sin rutina'}. Cambiarla no toca nada de lo que ya registraste: las
        series ya están escritas en su sesión.
      </p>

      {r.dias.length === 0 ? (
        <Vacio>Tu rutina no tiene días. Agregá el primero y ponele ejercicios.</Vacio>
      ) : (
        <div className="d-rejilla d-rejilla--2" style={{ marginTop: 'var(--e3)', alignItems: 'start' }}>
          {r.dias.map((item) => (
            <DiaEditable key={item.dia.id} r={r} item={item} />
          ))}
        </div>
      )}

      <div className="fila" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
        <button type="button" className="boton boton--chico" onClick={() => void r.agregarDia()}>
          <Icono nombre="mas" tamano={14} />
          Agregar un día
        </button>
        <MarcaGuardado visible={r.marca === 'dias'} />
      </div>

      {r.errorRutina && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="importante">{r.errorRutina}</Aviso>
        </div>
      )}

      <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
        Cada ejercicio entra con 3 series de 10 a 12 reps, y el peso lo ponés en la sesión. Todavía
        no se reordena: para cambiar el orden hay que quitar y volver a agregar. Quitar saca la fila
        de este dispositivo; la planilla todavía no borra filas, así que lo que ya se subió puede
        volver en la próxima sincronización y ahí se borra a mano.
      </p>

      <hr className="separador" />
      <BloqueGimnasio r={r} />

      <hr className="separador" />
      <BloquePaquete r={r} />
    </section>
  );
}

function DiaEditable({ r, item }: { r: ReturnType<typeof useRutina>; item: DiaDeRutina }): ReactNode {
  const [confirmando, setConfirmando] = useState(false);
  const disponibles = r.disponiblesPara(item.dia.id);

  return (
    <div className="tarjeta tarjeta--plana" style={{ border: '1px solid var(--hierro-borde)', padding: 'var(--e3)' }}>
      <input
        className="campo campo--texto"
        style={{ minHeight: 40, fontWeight: 600 }}
        defaultValue={item.dia.nombre}
        onBlur={(e) => void r.renombrarDia(item.dia, e.target.value)}
        aria-label={`Nombre del día «${item.dia.nombre}»`}
      />
      <MarcaGuardado visible={r.marca === `dia:${item.dia.id}`} />

      <table className="tabla" style={{ marginTop: 'var(--e2)' }}>
        <tbody>
          {item.filas.map(({ fila, ejercicio }) => (
            <tr key={fila.id}>
              <td>
                {ejercicio?.nombre ?? fila.ejercicio_id}
                {!ejercicio && (
                  <span className="nota--fina" style={{ display: 'block' }}>
                    no está en el catálogo de este gimnasio
                  </span>
                )}
              </td>
              <td style={{ width: 40, textAlign: 'right' }}>
                <button
                  type="button"
                  className="boton boton--chico boton--fantasma"
                  style={{ minHeight: 30, padding: '0 8px' }}
                  onClick={() => void r.quitarEjercicio(fila)}
                  aria-label={`Quitar ${ejercicio?.nombre ?? fila.ejercicio_id} de ${item.dia.nombre}`}
                >
                  <Icono nombre="cerrar" tamano={13} />
                </button>
              </td>
            </tr>
          ))}
          {item.filas.length === 0 && (
            <tr>
              <td colSpan={2} className="nota--fina" style={{ color: 'var(--ambar)' }}>
                Este día no tiene ejercicios: la sesión abriría vacía.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Un `select` crudo y no `<Selector>`: la etiqueta de `Selector` deriva el id del texto, y
          con un día por tarjeta habría cuatro elementos con el mismo id en la página. */}
      <select
        className="campo campo--texto"
        style={{ marginTop: 'var(--e2)' }}
        value=""
        aria-label={`Agregar un ejercicio a ${item.dia.nombre}`}
        disabled={disponibles.length === 0}
        onChange={(e) => {
          if (e.target.value) void r.agregarEjercicio(item.dia.id, e.target.value);
        }}
      >
        <option value="">
          {disponibles.length === 0 ? 'Ya están todos los del catálogo' : 'Agregar un ejercicio…'}
        </option>
        {disponibles.map((e) => (
          <option key={e.id} value={e.id}>
            {e.nombre}
          </option>
        ))}
      </select>

      {confirmando ? (
        <div style={{ marginTop: 'var(--e2)' }}>
          <Aviso tono="atencion" titulo={`¿Quitar «${item.dia.nombre}»?`}>
            Sale de la rutina y deja de aparecer al abrir una sesión. Lo que ya registraste ese día
            no se toca: las series siguen en tu historial.
            <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
              <button
                type="button"
                className="boton boton--chico boton--peligro"
                onClick={() => {
                  setConfirmando(false);
                  void r.quitarDia(item.dia);
                }}
              >
                Sí, quitarlo
              </button>
              <button type="button" className="boton boton--chico" onClick={() => setConfirmando(false)}>
                Cancelar
              </button>
            </div>
          </Aviso>
        </div>
      ) : (
        <button
          type="button"
          className="boton boton--chico boton--fantasma"
          style={{ marginTop: 'var(--e2)' }}
          onClick={() => setConfirmando(true)}
        >
          Quitar el día
        </button>
      )}
    </div>
  );
}

function BloqueGimnasio({ r }: { r: ReturnType<typeof useRutina> }): ReactNode {
  if (!r.gimnasio) {
    return (
      <>
        <span className="eyebrow">Gimnasio</span>
        <Vacio>Todavía no hay un gimnasio cargado.</Vacio>
      </>
    );
  }

  return (
    <>
      <div className="fila fila--entre">
        <span className="eyebrow">Gimnasio · {r.gimnasio.nombre}</span>
        <Insignia tono="agua">compartido</Insignia>
      </div>
      <p className="nota" style={{ marginTop: 4 }}>
        Los días de cierre salen del cálculo del colchón. Como el gimnasio lo comparten todos los
        que entrenan ahí, el cambio va directo al servidor.
      </p>

      <div className="fila" style={{ gap: 6, marginTop: 'var(--e3)', flexWrap: 'wrap' }}>
        {r.diasSemana.map((d) => (
          <button
            key={d.n}
            type="button"
            className={r.diasCierre.includes(d.n) ? 'boton boton--chico boton--principal' : 'boton boton--chico'}
            aria-pressed={r.diasCierre.includes(d.n)}
            onClick={() => r.alternarCierre(d.n)}
          >
            {d.etiqueta}
          </button>
        ))}
      </div>

      <div className="fila" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
        <button
          type="button"
          className="boton boton--chico"
          disabled={!r.hayCambiosGimnasio || r.subiendoGimnasio}
          onClick={() => void r.guardarGimnasio()}
        >
          {r.subiendoGimnasio ? 'Subiendo…' : 'Guardar los días de cierre'}
        </button>
        {!r.hayCambiosGimnasio && !r.subiendoGimnasio && (
          <span className="nota--fina">Tocá un día para cambiarlo y el botón se habilita.</span>
        )}
        <MarcaGuardado visible={r.marca === 'gimnasio'}>✓ Guardado y subido</MarcaGuardado>
      </div>

      {r.errorGimnasio && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="importante" titulo="No se pudo subir">
            {r.errorGimnasio} No se guarda a medias: guardarlo en local haría creer que los demás ya
            lo ven.
            <button
              type="button"
              className="boton boton--chico"
              style={{ marginTop: 8 }}
              onClick={() => void r.guardarGimnasio()}
              disabled={r.subiendoGimnasio}
            >
              Reintentar
            </button>
          </Aviso>
        </div>
      )}
    </>
  );
}

function BloquePaquete({ r }: { r: ReturnType<typeof useRutina> }): ReactNode {
  const { ir } = useRuta();

  return (
    <>
      <div className="fila fila--entre">
        <span className="eyebrow">Paquete de sesiones</span>
        {r.plan && (
          <button type="button" className="boton boton--chico boton--fantasma" onClick={() => ir('calendario')}>
            Ir a Plan
          </button>
        )}
      </div>
      <p className="nota" style={{ marginTop: 4 }}>
        {r.plan
          ? `Paquete de ${r.plan.sesiones_totales} sesiones, vence el ${fechaLarga(r.plan.fecha_vencimiento)}. Cuántas llevás usadas y cuánto colchón te queda lo dice Plan.`
          : 'No hay ningún paquete cargado. Sin paquete la app registra igual, pero no puede avisarte si te alcanza el tiempo.'}
      </p>

      <div className="d-rejilla d-rejilla--2" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
        <Campo
          etiqueta="Sesiones del paquete"
          inputMode="numeric"
          value={r.sesionesTotales}
          onChange={(e) => r.setSesionesTotales(e.target.value)}
        />
        <Campo
          etiqueta="Vence el"
          texto
          type="date"
          value={r.vencimiento}
          min={r.datos.hoy}
          onChange={(e) => r.setVencimiento(e.target.value)}
        />
      </div>

      <div className="fila" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
        <button type="button" className="boton boton--chico" disabled={!r.planValido} onClick={() => void r.guardarPaquete()}>
          {r.plan ? 'Guardar el paquete' : 'Cargar un paquete'}
        </button>
        {r.queFaltaPlan && <span className="nota--fina">{r.queFaltaPlan}</span>}
        <MarcaGuardado visible={r.marca === 'plan'} />
      </div>

      {r.errorPlan && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="importante">{r.errorPlan}</Aviso>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Objetivos — DESKTOP
// ---------------------------------------------------------------------------

function SeccionObjetivos({
  a,
  refSeccion,
}: {
  a: ReturnType<typeof useAjustes>;
  refSeccion: RefObject<HTMLElement | null>;
}): ReactNode {
  const p = a.datos.perfil;
  if (!p) return null;

  return (
    <section className="tarjeta" ref={refSeccion}>
      <span className="eyebrow">Objetivos</span>

      <span className="eyebrow etiqueta" style={{ marginTop: 'var(--e3)' }}>
        Series semanales por grupo
      </span>
      <div className="d-rejilla d-rejilla--3" style={{ gap: 'var(--e3)' }}>
        {a.grupos.map((g) => (
          <div key={g}>
            <Campo
              etiqueta={g}
              inputMode="numeric"
              placeholder="—"
              defaultValue={p.objetivo_series_semanales[g] ?? ''}
              onBlur={(e) => {
                const n = Number(e.target.value);
                void a.setObjetivo(g, Number.isFinite(n) && n > 0 ? n : null);
              }}
            />
            <MarcaGuardado visible={a.guardado === `grupo:${g}`} />
          </div>
        ))}
      </div>
      <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
        El objetivo colorea el mapa de calor. Sin objetivo, la app no inventa uno.
      </p>

      <hr className="separador" />

      <span className="eyebrow etiqueta">Comida</span>
      <div className="d-rejilla d-rejilla--2" style={{ gap: 'var(--e3)' }}>
        <div>
          <Campo
            etiqueta="Calorías por día (kcal)"
            inputMode="numeric"
            placeholder="—"
            defaultValue={p.objetivo_kcal ?? ''}
            // El campo se deja en lo que quedó guardado: si el texto no era un número, mostrarlo
            // igual haría creer que hay un objetivo que no existe.
            onBlur={(e) => {
              const valor = objetivoOpcional(e.target.value);
              e.target.value = valor === null ? '' : String(valor);
              void a.guardar({ objetivo_kcal: valor }, 'kcal');
            }}
          />
          <MarcaGuardado visible={a.guardado === 'kcal'} />
        </div>
        <div>
          <Campo
            etiqueta="Proteína por día (g)"
            inputMode="numeric"
            placeholder="—"
            defaultValue={p.objetivo_proteina_g ?? ''}
            onBlur={(e) => {
              const valor = objetivoOpcional(e.target.value);
              e.target.value = valor === null ? '' : String(valor);
              void a.guardar({ objetivo_proteina_g: valor }, 'proteina');
            }}
          />
          <MarcaGuardado visible={a.guardado === 'proteina'} />
        </div>
      </div>
      <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
        La app no sugiere un número: este objetivo es tuyo. Vacío = sin objetivo.
      </p>
    </section>
  );
}

/**
 * §2 — el mapa de máquinas es del gimnasio, compartido. Lo que se registra acá lo heredan los
 * demás usuarios de ese gimnasio, y no queda registro de quién lo aportó.
 */
function MapaDeMaquinas({ a }: { a: ReturnType<typeof useAjustes> }): ReactNode {
  const gimnasio = a.datos.gimnasioActivo;
  const [ejercicioId, setEjercicioId] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [unidad, setUnidad] = useState<Unidad>('lb');
  const [incremento, setIncremento] = useState('5');
  const [maximo, setMaximo] = useState('');
  const [estado, setEstado] = useState<string | null>(null);

  const sinUbicacion = a.datos.ejercicios.filter(
    (e) => e.tipo === 'maquina' && !a.datos.maquinaDe(e.id),
  );

  if (!gimnasio) {
    return (
      <section className="tarjeta">
        <span className="eyebrow">Mapa de máquinas</span>
        <Vacio>Falta configurar un gimnasio.</Vacio>
      </section>
    );
  }

  return (
    <section className="tarjeta">
      <div className="fila fila--entre">
        <span className="eyebrow">Mapa de máquinas · {gimnasio.nombre}</span>
        <Insignia tono="agua">compartido</Insignia>
      </div>
      <p className="nota" style={{ marginTop: 4 }}>
        Lo que registres acá lo ven todos los que entrenan en este gimnasio. No queda registro de
        quién lo aportó, y no expone ningún dato personal.
      </p>

      <table className="tabla" style={{ marginTop: 'var(--e3)' }}>
        <thead>
          <tr>
            <th>Ubicación</th>
            <th>Ejercicio</th>
            <th className="num">Incremento</th>
            <th className="num">Máx</th>
          </tr>
        </thead>
        <tbody>
          {a.datos.maquinas
            .filter((m) => m.gimnasio_id === gimnasio.id)
            .map((m) => (
              <tr key={m.id}>
                <td>{m.etiqueta_ubicacion}</td>
                <td className="apagado">{a.datos.ejerciciosPorId.get(m.ejercicio_id)?.nombre ?? m.ejercicio_id}</td>
                <td className="num">
                  {formatearNumero(m.incremento_placa)} {m.unidad_placa}
                </td>
                <td className="num">{m.placa_max === null ? '—' : formatearNumero(m.placa_max)}</td>
              </tr>
            ))}
          {a.datos.maquinas.filter((m) => m.gimnasio_id === gimnasio.id).length === 0 && (
            <tr>
              <td colSpan={4}>
                <Vacio>Todavía no hay ubicaciones registradas.</Vacio>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <hr className="separador" />

      <span className="eyebrow">Agregar una ubicación</span>
      <div className="d-rejilla d-rejilla--2" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
        <Selector etiqueta="Ejercicio" value={ejercicioId} onChange={(e) => setEjercicioId(e.target.value)}>
          <option value="">Elegí uno…</option>
          {sinUbicacion.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nombre}
            </option>
          ))}
        </Selector>
        <Campo
          etiqueta="Etiqueta de ubicación"
          texto
          placeholder="Máquina baranda 3"
          value={etiqueta}
          onChange={(e) => setEtiqueta(e.target.value)}
        />
        <Selector etiqueta="Unidad de la placa" value={unidad} onChange={(e) => setUnidad(e.target.value as Unidad)}>
          <option value="lb">Libras</option>
          <option value="kg">Kilos</option>
        </Selector>
        <Campo
          etiqueta="Incremento de placa"
          inputMode="decimal"
          value={incremento}
          onChange={(e) => setIncremento(e.target.value)}
          ayuda="De cuánto en cuánto sube esa máquina."
        />
        <Campo
          etiqueta="Peso máximo (opcional)"
          inputMode="decimal"
          value={maximo}
          onChange={(e) => setMaximo(e.target.value)}
        />
      </div>

      <button
        type="button"
        className="boton"
        style={{ marginTop: 'var(--e3)' }}
        disabled={!ejercicioId || !etiqueta.trim()}
        onClick={async () => {
          setEstado(null);
          try {
            await aportarMaquina({
              gimnasio_id: gimnasio.id,
              ejercicio_id: ejercicioId,
              etiqueta_ubicacion: etiqueta.trim(),
              unidad_placa: unidad,
              incremento_placa: Number(incremento.replace(',', '.')) || 5,
              placa_min: null,
              placa_max: maximo ? Number(maximo.replace(',', '.')) : null,
            });
            setEstado('Agregada. Se sincroniza en el próximo pull.');
            setEtiqueta('');
            setEjercicioId('');
          } catch (e) {
            setEstado(e instanceof Error ? e.message : 'No se pudo agregar.');
          }
        }}
      >
        Agregar al mapa del gimnasio
      </button>

      {estado && (
        <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
          {estado}
        </p>
      )}
      <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
        Necesita conexión: guardarlo local haría creer que los demás ya lo ven.
      </p>
    </section>
  );
}
